// PlanningManager.js - Orchestrateur principal avec support vue liste
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus } from 'lucide-react';
import PlanningView from './PlanningView';
import PlanningActionManager from './PlanningActionManager';
import ShareActionModal from './ShareActionModal';

const PlanningManager = ({ 
  actionPlan, 
  weeklyPlanning = [], 
  onCompleteAction,
  onUpdatePlanning,
  onUpdateElementaryAction,
  onDeleteElementaryAction,
  competenceData,
  userObjective,
  currentStepId,
  getCurrentStep,
  getStepNotes,
  onShareAction
}) => {
  // États principaux
  const [currentWeek, setCurrentWeek] = useState(0);
  const [selectedDay, setSelectedDay] = useState(new Date().getDay() - 1);
  const [editingAction, setEditingAction] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [draggedAction, setDraggedAction] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [planning, setPlanning] = useState([]);
  const [maxWeeksGenerated, setMaxWeeksGenerated] = useState(4);
  
  // États pour éviter les doubles appels
  const [isInitialized, setIsInitialized] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // État responsive
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [currentMobileDay, setCurrentMobileDay] = useState(new Date().getDay() - 1);

  // États pour le système de partage
  const [showShareModal, setShowShareModal] = useState(false);
  const [actionToShare, setActionToShare] = useState(null);

  // États pour la gestion de la priorité
  const [editingPriority, setEditingPriority] = useState(null);

  // ✅ REF pour la fonction de génération de détails
  const generateDetailsRef = useRef(null);

  // Hook pour détecter les changements de taille d'écran
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fonctions utilitaires de date
  const getWeekStartDate = (weekOffset = 0) => {
    const today = new Date();
    const currentDay = today.getDay();
    const diff = currentDay === 0 ? -6 : 1 - currentDay;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff + (weekOffset * 7));
    return monday;
  };

  const getDateForDay = (dayIndex, weekOffset = currentWeek) => {
    const weekStart = getWeekStartDate(weekOffset);
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + dayIndex);
    return date;
  };

  const isDateInPast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < today;
  };

  // Initialisation avec protection contre les re-renders
  useEffect(() => {
    if (isInitialized || isGenerating) {
      return;
    }
    
    const hasFirebaseData = competenceData?.weeklyPlanning?.length > 0;
    const hasPropsData = weeklyPlanning?.length > 0;
    const hasActionPlan = actionPlan?.steps?.length > 0;
    
    if (hasFirebaseData) {
      setPlanning(competenceData.weeklyPlanning);
      setMaxWeeksGenerated(Math.max(4, competenceData.weeklyPlanning.length));
      setIsInitialized(true);
    } else if (hasPropsData) {
      setPlanning(weeklyPlanning);
      setMaxWeeksGenerated(Math.max(4, weeklyPlanning.length));
      setIsInitialized(true);
    } else if (hasActionPlan && planning.length === 0) {
      setIsGenerating(true);
      generateInitialPlanningWithMultipleWeeks()
        .then(() => setIsInitialized(true))
        .catch(console.error)
        .finally(() => setIsGenerating(false));
    } else {
      setIsInitialized(true);
    }
  }, [
    competenceData?.weeklyPlanning, 
    weeklyPlanning, 
    actionPlan?.steps,
    isInitialized,
    isGenerating,
    planning.length
  ]);

  // Génération du planning initial
  const generateInitialPlanningWithMultipleWeeks = useCallback(async () => {
    if (!actionPlan?.steps || isGenerating) return;

    const elementaryActionsWeek1 = [];
    const elementaryActionsWeek2 = [];
    const elementaryActionsWeek3 = [];
    const elementaryActionsWeek4 = [];
    
    const today = new Date();
    const todayDayIndex = today.getDay() - 1;
    let currentWeekIndex = 0;
    let dayIndex = todayDayIndex < 0 ? 0 : todayDayIndex;

    actionPlan.steps.forEach((step, stepIndex) => {
      if (step.termine) return;

      const dayNames = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
      
      let targetDate = getDateForDay(dayIndex, currentWeekIndex);
      
      while (isDateInPast(targetDate)) {
        if (dayIndex >= 6) {
          currentWeekIndex++;
          dayIndex = 0;
        } else {
          dayIndex++;
        }
        targetDate = getDateForDay(dayIndex, currentWeekIndex);
      }
      
      const newAction = {
        id: `elem_${Date.now()}_${stepIndex}_${Math.random().toString(36).substr(2, 5)}`,
        stepId: step.id,
        stepTitle: step.titre,
        title: step.titre,
        description: step.description,
        duration: '1 heure',
        priority: 'medium',
        type: 'execution',
        day: dayNames[dayIndex],
        date: targetDate.toISOString().split('T')[0],
        timeSlot: `${9 + (stepIndex % 8)}:00-${10 + (stepIndex % 8)}:00`,
        completed: false,
        deliverable: `${step.titre}`,
        isElementary: true,
        memo: '',
        createdAt: new Date().toISOString()
      };

      if (currentWeekIndex === 0) {
        elementaryActionsWeek1.push(newAction);
      } else if (currentWeekIndex === 1) {
        elementaryActionsWeek2.push(newAction);
      } else if (currentWeekIndex === 2) {
        elementaryActionsWeek3.push(newAction);
      } else {
        elementaryActionsWeek4.push(newAction);
      }

      dayIndex = (dayIndex + 1) % 7;
      if (dayIndex === 0 && stepIndex < actionPlan.steps.length - 1) {
        currentWeekIndex++;
      }
    });

    const newPlanning = [
      { week: 1, theme: "Exécution du plan d'action - Semaine 1", elementaryActions: elementaryActionsWeek1, createdAt: new Date().toISOString() },
      { week: 2, theme: "Exécution du plan d'action - Semaine 2", elementaryActions: elementaryActionsWeek2, createdAt: new Date().toISOString() },
      { week: 3, theme: "Exécution du plan d'action - Semaine 3", elementaryActions: elementaryActionsWeek3, createdAt: new Date().toISOString() },
      { week: 4, theme: "Exécution du plan d'action - Semaine 4", elementaryActions: elementaryActionsWeek4, createdAt: new Date().toISOString() }
    ];

    setPlanning(newPlanning);
    setMaxWeeksGenerated(4);
    
    if (onUpdatePlanning) {
      try {
        await onUpdatePlanning(newPlanning);
      } catch (error) {
        console.error('Erreur sauvegarde planning initial:', error);
      }
    }

    return newPlanning;
  }, [actionPlan, onUpdatePlanning, getDateForDay, isDateInPast, isGenerating]);

  // Navigation des semaines
  const canGoToPreviousWeek = () => currentWeek > 0;
  const canGoToNextWeek = () => currentWeek < maxWeeksGenerated - 1;

  const goToPreviousWeek = () => {
    if (canGoToPreviousWeek()) {
      setCurrentWeek(currentWeek - 1);
    }
  };

  const goToNextWeek = () => {
    if (canGoToNextWeek()) {
      setCurrentWeek(currentWeek + 1);
    } else {
      generateNewWeek();
    }
  };

  const generateNewWeek = () => {
    const newWeekNumber = maxWeeksGenerated + 1;
    const newWeek = {
      week: newWeekNumber,
      theme: `Exécution du plan d'action - Semaine ${newWeekNumber}`,
      elementaryActions: [],
      createdAt: new Date().toISOString()
    };

    const updatedPlanning = [...planning, newWeek];
    setPlanning(updatedPlanning);
    setMaxWeeksGenerated(newWeekNumber);
    setCurrentWeek(newWeekNumber - 1);

    if (onUpdatePlanning) {
      onUpdatePlanning(updatedPlanning);
    }
  };

  // Navigation mobile jour par jour
  const goToPreviousDay = () => {
    if (currentMobileDay > 0) {
      setCurrentMobileDay(currentMobileDay - 1);
      setSelectedDay(currentMobileDay - 1);
    } else if (canGoToPreviousWeek()) {
      goToPreviousWeek();
      setCurrentMobileDay(6);
      setSelectedDay(6);
    }
  };

  const goToNextDay = () => {
    if (currentMobileDay < 6) {
      setCurrentMobileDay(currentMobileDay + 1);
      setSelectedDay(currentMobileDay + 1);
    } else if (canGoToNextWeek() || currentWeek < maxWeeksGenerated - 1) {
      goToNextWeek();
      setCurrentMobileDay(0);
      setSelectedDay(0);
    } else {
      generateNewWeek();
      setCurrentMobileDay(0);
      setSelectedDay(0);
    }
  };

  // Sauvegarde avec Firebase
  const savePlanning = useCallback(async (updatedPlanning) => {
    if (!updatedPlanning || !Array.isArray(updatedPlanning)) {
      return false;
    }
    
    if (onUpdatePlanning) {
      try {
        const result = await onUpdatePlanning(updatedPlanning);
        if (result) {
          setPlanning(updatedPlanning);
          return true;
        } else {
          return false;
        }
      } catch (error) {
        console.error('Erreur sauvegarde base de données:', error);
        alert('Erreur lors de la sauvegarde du planning. Vos modifications risquent d\'être perdues.');
        return false;
      }
    } else {
      setPlanning(updatedPlanning);
      return false;
    }
  }, [onUpdatePlanning]);

  // Gestionnaires pour le partage
  const handleShareAction = useCallback((action) => {
    console.log('Demande de partage pour l\'action:', action.title);
    
    if (onShareAction) {
      onShareAction(action);
    } else {
      setActionToShare(action);
      setShowShareModal(true);
    }
  }, [onShareAction]);

  const handleCloseShareModal = useCallback(() => {
    setShowShareModal(false);
    setActionToShare(null);
  }, []);

  const isActionShareable = useCallback((action) => {
    return action && 
           action.isElementary && 
           action.title && 
           action.title.trim().length > 0;
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <PlanningView
        planning={planning}
        currentWeek={currentWeek}
        selectedDay={selectedDay}
        viewMode={viewMode}
        isMobile={isMobile}
        currentMobileDay={currentMobileDay}
        maxWeeksGenerated={maxWeeksGenerated}
        draggedAction={draggedAction}
        currentStepId={currentStepId}
        getWeekStartDate={getWeekStartDate}
        getDateForDay={getDateForDay}
        canGoToPreviousWeek={canGoToPreviousWeek}
        canGoToNextWeek={canGoToNextWeek}
        goToPreviousWeek={goToPreviousWeek}
        goToNextWeek={goToNextWeek}
        goToPreviousDay={goToPreviousDay}
        goToNextDay={goToNextDay}
        setSelectedDay={setSelectedDay}
        setViewMode={setViewMode}
        setDraggedAction={setDraggedAction}
        setShowAddForm={setShowAddForm}
        onMoveAction={async (actionId, newDay, newWeekOffset, newDate) => {
          const originalPlanning = [...planning];
          const updatedPlanning = [...planning];
          let targetWeekIndex = currentWeek;
          
          if (newWeekOffset !== null) {
            targetWeekIndex = newWeekOffset;
          }

          const weekData = updatedPlanning[targetWeekIndex];
          
          if (weekData?.elementaryActions) {
            let actionIndex = weekData.elementaryActions.findIndex(a => a.id === actionId);
            let sourceWeekData = weekData;
            
            if (actionIndex === -1) {
              for (let i = 0; i < updatedPlanning.length; i++) {
                const searchWeek = updatedPlanning[i];
                actionIndex = searchWeek.elementaryActions.findIndex(a => a.id === actionId);
                if (actionIndex !== -1) {
                  sourceWeekData = searchWeek;
                  break;
                }
              }
            }
            
            if (actionIndex !== -1) {
              const action = sourceWeekData.elementaryActions[actionIndex];
              
              if (sourceWeekData !== weekData) {
                sourceWeekData.elementaryActions = sourceWeekData.elementaryActions.filter(a => a.id !== actionId);
              }
              
              const dayNames = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
              const newDayIndex = dayNames.indexOf(newDay);
              
              const finalDate = newDate || getDateForDay(newDayIndex, targetWeekIndex);
              const finalDay = dayNames[finalDate.getDay() === 0 ? 6 : finalDate.getDay() - 1];
              
              const updatedAction = {
                ...action,
                day: finalDay,
                date: finalDate.toISOString().split('T')[0]
              };
              
              if (sourceWeekData === weekData) {
                weekData.elementaryActions[actionIndex] = updatedAction;
              } else {
                weekData.elementaryActions.push(updatedAction);
              }
              
              try {
                const success = await savePlanning(updatedPlanning);
                
                if (success) {
                  if (onUpdateElementaryAction) {
                    await onUpdateElementaryAction(actionId, updatedAction);
                  }
                } else {
                  setPlanning(originalPlanning);
                }
              } catch (error) {
                console.error('Erreur lors du déplacement:', error);
                setPlanning(originalPlanning);
              }
            }
          }
        }}
        onCompleteAction={async (actionId) => {
          const updatedPlanning = [...planning];
          
          for (let weekIndex = 0; weekIndex < updatedPlanning.length; weekIndex++) {
            const weekData = updatedPlanning[weekIndex];
            if (weekData?.elementaryActions) {
              const actionIndex = weekData.elementaryActions.findIndex(a => a.id === actionId);
              if (actionIndex !== -1) {
                weekData.elementaryActions[actionIndex].completed = !weekData.elementaryActions[actionIndex].completed;
                weekData.elementaryActions[actionIndex].completedAt = weekData.elementaryActions[actionIndex].completed 
                  ? new Date().toISOString() : null;
                
                await savePlanning(updatedPlanning);
                
                if (onCompleteAction) {
                  onCompleteAction(actionId);
                }
                return;
              }
            }
          }
        }}
        onEditAction={setEditingAction}
        onDeleteAction={setShowDeleteConfirm}
        onGenerateDetails={(actionId) => {
          // ✅ CORRECTION : Appeler la vraie fonction de génération de détails
          console.log('Demande de génération de détails pour:', actionId);
          if (generateDetailsRef.current) {
            generateDetailsRef.current(actionId);
          } else {
            console.warn('Fonction de génération de détails non disponible');
          }
        }}
        onShareAction={handleShareAction}
        isGeneratingDetails={false}
        editingPriority={editingPriority}
        setEditingPriority={setEditingPriority}
        onChangePriority={async (actionId, newPriority) => {
          const updatedPlanning = [...planning];
          
          for (let weekIndex = 0; weekIndex < updatedPlanning.length; weekIndex++) {
            const weekData = updatedPlanning[weekIndex];
            if (weekData?.elementaryActions) {
              const actionIndex = weekData.elementaryActions.findIndex(a => a.id === actionId);
              if (actionIndex !== -1) {
                weekData.elementaryActions[actionIndex].priority = newPriority;
                weekData.elementaryActions[actionIndex].updatedAt = new Date().toISOString();
                
                await savePlanning(updatedPlanning);
                setEditingPriority(null);
                return;
              }
            }
          }
        }}
        isActionShareable={isActionShareable}
      />

      <PlanningActionManager
        planning={planning}
        currentWeek={currentWeek}
        selectedDay={selectedDay}
        isMobile={isMobile}
        currentMobileDay={currentMobileDay}
        editingAction={editingAction}
        showAddForm={showAddForm}
        showDeleteConfirm={showDeleteConfirm}
        actionPlan={actionPlan}
        maxWeeksGenerated={maxWeeksGenerated}
        viewMode={viewMode}
        currentStepId={currentStepId}
        getDateForDay={getDateForDay}
        isDateInPast={isDateInPast}
        getWeekStartDate={getWeekStartDate}
        setPlanning={setPlanning}
        setMaxWeeksGenerated={setMaxWeeksGenerated}
        setEditingAction={setEditingAction}
        setShowAddForm={setShowAddForm}
        setShowDeleteConfirm={setShowDeleteConfirm}
        savePlanning={savePlanning}
        onCompleteAction={onCompleteAction}
        onDeleteElementaryAction={onDeleteElementaryAction}
        onUpdateElementaryAction={onUpdateElementaryAction}
        handleShareAction={handleShareAction}
        isActionShareable={isActionShareable}
        onGenerateDetailsCallback={(fn) => {
          // ✅ Stocker la référence à la fonction de génération
          generateDetailsRef.current = fn;
          console.log('Fonction de génération de détails enregistrée');
        }}
      />

      {!onShareAction && showShareModal && actionToShare && (
        <ShareActionModal
          show={showShareModal}
          onClose={handleCloseShareModal}
          action={actionToShare}
          userObjective={userObjective}
          currentStep={getCurrentStep?.()}
          stepNotes={getStepNotes?.(currentStepId)}
          actionPlan={actionPlan}
        />
      )}
    </div>
  );
};

export default PlanningManager;